import Integration, {
  DeviceIntegrationProvider,
  ExternallyManagedIntegration,
  RemoteIntegrationProvider,
  IntegrationStatus,
  IntegrationProvider,
} from '@models/Integration';
import { OrderedMap } from 'immutable';
import type { ID, ReduxAction } from '@app/types';
import { AppState } from '@reducers/rootReducer';
import Occasion from '@support/Occasion';
import BaseObject from '@models/BaseObject';
import { electronPlatformToAPIPlatform } from '@actions/deviceIntegrations';

export type UserIntegrationsState = UserIntegrations;

class CardDavIntegration extends BaseObject {
  // is_ready: boolean;
  // first_sync: string;
  // last_sync: string;
  username: string;
  password?: string;
  gettingPassword?: boolean;

  constructor(props: any = {}) {
    super(props);
    this.username = props.username;
    this.password = props.access_token || props.password;
    this.gettingPassword = props.gettingPassword;
  }
}

interface EnabledIntegration {
  integration?: Integration;
  enablingProvider?: DeviceIntegrationProvider;
}

const deviceIntegrationOptions: DeviceIntegrationProvider[] = [
  'linkedin',
  'facebook',
  'twitter-platforms',
  'apple-phone-log',
  'apple-messages',
  'apple-contacts',
  'whatsapp',
];

export interface DeviceIntegrationStatus {
  supported: boolean;
  /**
   * Does the user have this integration set up on this device
   */
  enabled: boolean;
  syncing: boolean;
  error?: { message: string; type: string };

  // Does the user have this integration set up anywhere?
  connected: boolean;

  // current syncing progress
  progress?: number;
}

export type ProfileUpdateStatusType = 'updating' | 'success' | 'error';

export interface ProfileUpdateStatus {
  type: ProfileUpdateStatusType;
  message?: string;
}

class UserIntegrations extends BaseObject {
  connected: Integration[];
  onDeviceStatuses?: Record<DeviceIntegrationProvider, DeviceIntegrationStatus>;
  contactProfileUpdateStatuses?: OrderedMap<ID, ProfileUpdateStatus>;
  cardDav?: CardDavIntegration;
  externallyManagedIntegrations: OrderedMap<ID, Integration>;
  deviceIntegrationSetupAborted?: boolean;

  constructor(props: any = {}) {
    super(props);
    this.connected = props.connected || [];
    this.onDeviceStatuses = props.onDeviceStatuses || {};
    this.contactProfileUpdateStatuses = props.contactProfileUpdateStatuses || OrderedMap();
    this.cardDav = props.cardDav;
    this.externallyManagedIntegrations = props.externallyManagedIntegrations || OrderedMap();
    this.deviceIntegrationSetupAborted = !!props.deviceIntegrationSetupAborted;

    if (props.connected && props.onDeviceStatuses) {
      // @ts-ignore
      this.onDeviceStatuses = Object.keys(props.onDeviceStatuses).reduce((statuses, provider) => {
        return {
          ...statuses,
          [provider]: {
            ...props.onDeviceStatuses[provider],
            connected: props.connected.some((i) => i.provider === provider),
          },
        };
      }, {});
    }
  }

  get all(): Integration[] {
    return this.connected.filter((i) => !i.isDeleting);
  }

  get setupDeviceIntegrations(): DeviceIntegrationProvider[] {
    return deviceIntegrationOptions.filter((provider) => this.hasDeviceIntegration(provider));
  }

  get remote(): Integration[] {
    return this.all.filter((i) => !i.isOnDevice);
  }

  get enabledIntegrations(): EnabledIntegration[] {
    const enabledRemoteIntegrations: EnabledIntegration[] = this.remote.map((integration) => ({
      integration,
    }));
    const enabledDeviceIntegrations: EnabledIntegration[] = this.setupDeviceIntegrations.map(
      (provider) => {
        const integration = this.getIntegrationsForProvider(provider)?.[0];
        if (!integration) {
          return { enablingProvider: provider as DeviceIntegrationProvider };
        } else {
          return { integration };
        }
      },
    );
    return enabledRemoteIntegrations.concat(enabledDeviceIntegrations);
  }

  get connectOnDeviceOptions(): IntegrationProvider[] {
    return (deviceIntegrationOptions as IntegrationProvider[])
      .filter((provider) => !this.hasDeviceIntegration(provider as DeviceIntegrationProvider))
      .concat(['linkedin-csv', 'csv']);
  }

  getIntegration(integrationID: ID): Integration | undefined {
    return this.connected.find((i) => i.id === integrationID);
  }

  getStatusOnDevice(provider: DeviceIntegrationProvider): DeviceIntegrationStatus | undefined {
    return this.onDeviceStatuses[provider];
  }

  getIntegrationsForProvider(
    provider: RemoteIntegrationProvider | DeviceIntegrationProvider,
  ): Integration[] | undefined {
    return this.connected.filter((i) => i.provider === provider);
  }

  hasEmailIntegration(): boolean {
    return this.connected.some((i) => i.type === 'email');
  }

  hasCalendarIntegration(): boolean {
    return this.connected.some((i) => i.type === 'calendar');
  }

  hasIntegration(provider: RemoteIntegrationProvider | DeviceIntegrationProvider): boolean {
    return this.connected.some((i) => i.provider === provider);
  }

  hasDeviceIntegration(provider: DeviceIntegrationProvider): boolean {
    const integrationsForProvider = this.getIntegrationsForProvider(provider);
    const integration = integrationsForProvider?.[0];
    return (this.onDeviceStatuses[provider]?.enabled || !!integration) && !integration?.isDeleting;
  }

  hasExternallyManagedIntegration(provider: ExternallyManagedIntegration): boolean {
    return this.externallyManagedIntegrations.valueSeq().some((i) => i.provider === provider);
  }

  applyStatus(provider: DeviceIntegrationProvider, status: Partial<DeviceIntegrationStatus>) {
    if (!this.onDeviceStatuses[provider]) {
      return this.onDeviceStatuses;
    }

    return {
      ...this.onDeviceStatuses,
      [provider]: { ...this.onDeviceStatuses[provider], ...status },
    };
  }

  updateIntegration(
    id: ID,
    integration: Omit<Partial<Integration>, 'status'> & { status?: Partial<IntegrationStatus> },
    status: Partial<DeviceIntegrationStatus> = {},
  ) {
    return this.copyWith({
      connected: this.connected.map((i) => (i.id === id ? i.copyWith(integration) : i)),
      onDeviceStatuses: this.applyStatus(integration.provider as DeviceIntegrationProvider, status),
    });
  }

  updateIntegrationForProvider(
    provider: RemoteIntegrationProvider,
    integration: Partial<Integration>,
  ) {
    return this.copyWith({
      connected: this.connected.map((i) => (i.provider === provider ? i.copyWith(integration) : i)),
    });
  }

  updateDeviceIntegrationStatus(
    provider: DeviceIntegrationProvider,
    onDeviceStatus: Partial<DeviceIntegrationStatus>,
    integrationStatus?: Partial<IntegrationStatus>,
  ) {
    let connected = this.connected;
    const integrationsForProvider = this.getIntegrationsForProvider(provider);

    if (!!integrationStatus) {
      if (integrationsForProvider.length > 0) {
        connected = this.connected.map((i) =>
          i.provider === provider
            ? i.copyWith({ status: integrationStatus as IntegrationStatus })
            : i,
        );
      } else {
        connected = this.connected.concat(
          new Integration({ provider, status: integrationStatus as IntegrationStatus }),
        );
      }
    }

    return this.copyWith({
      connected,
      onDeviceStatuses: this.applyStatus(provider, onDeviceStatus),
    });
  }

  removeIntegration(id: ID) {
    const integration = this.getIntegration(id);
    let onDeviceStatuses = this.onDeviceStatuses;

    if (integration?.isOnDevice) {
      onDeviceStatuses = this.applyStatus(integration.provider as DeviceIntegrationProvider, {
        enabled: false,
        syncing: false,
        connected: false,
      });
    }

    return this.copyWith({
      connected: this.connected.filter((i) => i.id != id),
      onDeviceStatuses,
    });
  }

  updateContactProfileUpdateStatus(
    contactID: ID,
    status: ProfileUpdateStatusType,
    message?: string,
  ) {
    return this.copyWith({
      contactProfileUpdateStatuses: this.contactProfileUpdateStatuses.set(contactID, {
        type: status,
        message,
      }),
    });
  }

  removeContactProfileUpdateStatus(contactID: ID) {
    return this.copyWith({
      contactProfileUpdateStatuses: this.contactProfileUpdateStatuses.delete(contactID),
    });
  }

  clearContactProfileUpdateStatuses() {
    return this.copyWith({ contactProfileUpdateStatuses: OrderedMap() });
  }
}

const initialUserIntegrationsState = new UserIntegrations({
  onDeviceStatuses: window.desktopIntegrations?.supportedIntegrations?.reduce(
    (onDeviceStatuses, integration) => {
      return {
        ...onDeviceStatuses,
        [electronPlatformToAPIPlatform(integration.platform)]: {
          connected: false,
          enabled: false,
          supported: true,
          syncing: false,
        },
      };
    },
    {},
  ),
});

export const selectIntegrations = (state: AppState) => state.user.integrations;
export const selectAllIntegrations = (state: AppState) => selectIntegrations(state).all;
export const selectIntegrationByID = (id: ID) => (state: AppState) =>
  selectIntegrations(state).getIntegration(id);

export const selectContactProfileUpdateStatus = (contactID: ID) => (state: AppState) =>
  selectIntegrations(state).contactProfileUpdateStatuses.get(contactID);

export const selectCardDavIntegration = (state: AppState) => selectIntegrations(state).cardDav;

export interface UserIntegrationPresence {
  hasEmail: boolean;
  hasCalendar: boolean;
  hasTwitter: boolean;
  hasWhatsapp: boolean;
  hasLinkedin: boolean;
  hasFacebook: boolean;
  hasMessages: boolean;
  hasContacts: boolean;
  hasZapier: boolean;
}

export const selectIntegrationPresence = (state: AppState): UserIntegrationPresence => {
  const integrations = selectIntegrations(state);

  return {
    hasEmail: integrations.hasEmailIntegration(),
    hasCalendar: integrations.hasCalendarIntegration(),
    hasTwitter: integrations.hasIntegration('twitter-platforms'),
    hasWhatsapp: integrations.hasIntegration('whatsapp'),
    hasLinkedin: integrations.hasIntegration('linkedin'),
    hasFacebook: integrations.hasIntegration('facebook'),
    hasMessages: integrations.hasIntegration('apple-messages'),
    hasContacts: integrations.hasIntegration('apple-contacts'),
    hasZapier: integrations.hasExternallyManagedIntegration('zapier'),
  };
};

const loadingIntegration = new Integration({ isFetching: true });

// This only returns the first integration of the specified platform
// Arguably we should only ever be selecting by integration ID. *code smell*
export const selectIntegrationByPlatform =
  (platform: DeviceIntegrationProvider | RemoteIntegrationProvider) => (state: AppState) => {
    const integrationsState = selectIntegrations(state);

    if (!integrationsState.isLoaded) {
      return loadingIntegration;
    }

    return integrationsState.all.find((integration) => integration.provider === platform);
  };

export const selectIntegrationStatusOnDevice =
  (provider: DeviceIntegrationProvider) => (state: AppState) => {
    const integrationsState = selectIntegrations(state);
    return integrationsState.getStatusOnDevice(provider);
  };

// TODO: either 'twitter' OR 'twitter-platforms'
export const selectUserHasTwitter = (state: AppState) => {
  const twitterIntegration = selectIntegrationByPlatform('twitter-platforms')(state);
  return twitterIntegration && !twitterIntegration.isFetching ? true : false;
};

export function userIntegrationsReducer(
  state: UserIntegrationsState = initialUserIntegrationsState,
  { type, payload }: ReduxAction,
) {
  switch (type) {
    case 'FETCH_ALL_INTEGRATIONS_SUCCEEDED':
      const loadedIntegrationForNewlySyncedPlatform = state.getIntegrationsForProvider(
        payload.newlySyncedPlatform,
      )?.[0];

      return state.copyWith({
        connected: payload.response
          .map((i) => {
            if (
              !!loadedIntegrationForNewlySyncedPlatform &&
              i.provider === payload.newlySyncedPlatform &&
              !!loadedIntegrationForNewlySyncedPlatform.status?.total
            ) {
              return new Integration({
                ...i,
                updated: new Occasion(),
                status: {
                  ...(i.status || {}),
                  total: loadedIntegrationForNewlySyncedPlatform.status?.total,
                },
              });
            }

            return new Integration({ ...i, updated: new Occasion() });
          })
          .filter((i) => i.hasValidScopes || i.isOnDevice),
        updated: new Occasion(),
        onDeviceStatuses: payload.newlySyncedPlatform
          ? state.applyStatus(payload.newlySyncedPlatform, { syncing: false, progress: undefined })
          : state.onDeviceStatuses,
      });

    // should handle failures here...
    case 'DELETE_INTEGRATION_REQUESTED':
      return state.updateIntegration(payload.id, { isDeleting: true });

    case 'DELETE_INTEGRATION_SUCCEEDED':
      return state.removeIntegration(payload.integration.id);

    case 'UPDATE_INTEGRATION_REQUESTED':
      return state.updateIntegrationForProvider(payload.provider, payload.update);

    // Need to better differentiate between remote sync and device sync...
    case 'CREATE_INTEGRATION_SYNC_REQUESTED':
      return state.updateIntegration(payload.id, { status: { is_syncing: true } });

    case 'CREATE_INTEGRATION_SYNC_FAILED':
      // TODO: do we get back an error message from API?
      return state.updateIntegration(payload.integration.id, {
        status: { is_syncing: false, status: 'error', message: 'Failed to start sync' },
      });

    // Everything below here is specific to the device integrations
    case 'SET_INTEGRATION_AUTHORIZATION':
      const updatedAuthState = state.updateDeviceIntegrationStatus(payload.provider, {
        enabled: payload.authed,
      });

      if (payload.provider === 'linkedin' && !payload.authed) {
        return updatedAuthState.clearContactProfileUpdateStatuses();
      } else {
        return updatedAuthState;
      }

    case 'INTEGRATION_STARTED_SYNC':
      return state.updateDeviceIntegrationStatus(
        payload.provider,
        { syncing: true, connected: true, enabled: true },
        { connected: true },
      );

    case 'FETCH_INTEGRATION_SUCCEEDED':
      return state.updateIntegration(payload.response.id, payload.response, {
        syncing: false,
        progress: undefined,
      });

    case 'INTEGRATION_SYNC_PROGRESS':
      if (!!payload.total) {
        return state.updateDeviceIntegrationStatus(payload.provider, {
          progress: (payload.complete || 0) / payload.total,
        });
      }
      return state;

    case 'INTEGRATION_COMPLETED_SYNC':
      return state.updateDeviceIntegrationStatus(
        payload.provider,
        { syncing: false },
        { total: payload.total, last_import: Date.now().toString() },
      );

    case 'DEVICE_INTEGRATION_REMOVED':
      return state.updateDeviceIntegrationStatus(payload.provider, {
        enabled: false,
        connected: false,
        syncing: false,
      });

    case 'DEVICE_INTEGRATION_ERRORED':
      const erroredIntegrationState = state.updateDeviceIntegrationStatus(payload.provider, {
        syncing: false,
        error: { type: payload.type, message: payload.error },
      });

      if (payload.provider === 'linkedin') {
        return erroredIntegrationState.clearContactProfileUpdateStatuses();
      }

      return erroredIntegrationState;

    case 'DEVICE_INTEGRATION_SETUP_ABORTED':
      return state
        .updateDeviceIntegrationStatus(payload.provider, { syncing: false })
        .copyWith({ deviceIntegrationSetupAborted: true });

    case 'CLEAR_DEVICE_INTEGRATION_SETUP_ABORTED':
      return state.copyWith({ deviceIntegrationSetupAborted: false });

    // Updating the profile of a contact using the LinkedIn integration
    case 'UPDATE_CONTACT_PROFILE_REQUESTED':
      return state.updateContactProfileUpdateStatus(payload.contactID, 'updating');

    case 'UPDATE_CONTACT_PROFILE_SUCCEEDED':
      return state.updateContactProfileUpdateStatus(
        payload.contactID,
        'success',
        payload.isPartial ? 'Partial update' : undefined,
      );

    case 'UPDATE_CONTACT_PROFILE_FAILED':
      return state.updateContactProfileUpdateStatus(payload.contactID, 'error');

    case 'CLEAR_PROFILE_UPDATE_STATUS':
      return state.removeContactProfileUpdateStatus(payload.contactID);

    // External integrations
    case 'FETCH_ALL_EXTERNAL_INTEGRATIONS_SUCCEEDED':
      return state.copyWith({
        externallyManagedIntegrations: OrderedMap(
          payload.response
            .filter((int) => !!int?.provider && int?.provider !== 'carddav')
            .map((int) => [int.id, new Integration(int)]),
        ),
      });

    case 'DELETE_EXTERNAL_INTEGRATION_REQUESTED':
      return state.copyWith({
        externallyManagedIntegrations: state.externallyManagedIntegrations.filter(
          (int) => int.id !== payload.id,
        ),
      });

    case 'UPDATE_EXTERNAL_INTEGRATION_REQUESTED':
      return state.copyWith({
        externallyManagedIntegrations: state.externallyManagedIntegrations.update(
          payload.id,
          (int) => {
            return int.copyWith({ isFetching: true });
          },
        ),
      });

    case 'UPDATE_EXTERNAL_INTEGRATION_SUCCEEDED':
      return state.copyWith({
        externallyManagedIntegrations: state.externallyManagedIntegrations.update(
          payload.id,
          (int) => {
            return int.copyWith({ isFetching: false, status: { last_import: new Date() } });
          },
        ),
      });

    // CardDav
    case 'CREATE_CARDDAV_INTEGRATION_REQUESTED':
    case 'FETCH_CARDDAV_INTEGRATION_REQUESTED':
      return state.copyWith({ cardDav: new CardDavIntegration({ isFetching: true }) });

    case 'CREATE_CARDDAV_INTEGRATION_SUCCEEDED':
    case 'FETCH_CARDDAV_INTEGRATION_SUCCEEDED':
      return state.copyWith({ cardDav: new CardDavIntegration(payload.response) });

    case 'CREATE_CARDDAV_LOGIN_REQUESTED':
      return state.copyWith({ cardDav: state.cardDav?.copyWith({ gettingPassword: true }) });

    case 'CREATE_CARDDAV_LOGIN_SUCCEEDED':
      return state.copyWith({
        cardDav: state.cardDav?.copyWith({ gettingPassword: false, ...(payload.response || {}) }),
      });

    case 'DELETE_CARDDAV_INTEGRATION_SUCCEEDED':
    case 'FETCH_CARDDAV_INTEGRATION_FAILED':
      return state.copyWith({ cardDav: undefined });

    default:
      return state;
  }
}
